import * as React from 'react';
import { LargeCardFieldsFragment } from 'types/generated';
import { ImageWithFocalPoint } from '../ImageWithFocalPoint';
import Image from 'next/image';
import { Subheading } from 'components/src/typography';
import { getOr } from '@/utils/options';
import { NextLink } from 'components/src/link';
import ISKFormat from '../common/ISKFormat';
import CardButton from './CardButton';
import { richTextFromMarkdown } from '@contentful/rich-text-from-markdown';
import { Document } from '@contentful/rich-text-types';

import RichTextContainer from './RichTextContainer';

type Props = {
  card: LargeCardFieldsFragment;
  onClick?: () => void;
  animateOnSuccess?: boolean;
};

const Card = ({ card, animateOnSuccess = true, onClick }: Props) => {
  const [markdownDocument, setMarkdownDocument] = React.useState<Document | undefined>(undefined);

  React.useEffect(() => {
    const fetchMarkdownDocument = async () => {
      const document = await richTextFromMarkdown(getOr(card.description, ''));
      setMarkdownDocument(document);
    };
    fetchMarkdownDocument();
  }, [card.description]);

  return (
    <div className="flex flex-col overflow-hidden rounded-28 bg-white-100">
      {card.image && (
        <div className="relative max-h-[160px] min-h-[160px] flex-1 md:max-h-[220px] md:min-h-[220px]">
          <ImageWithFocalPoint image={card.image} className="rounded-t-28" sizes={{ default: '420px' }} />
        </div>
      )}
      <div className="flex flex-1 flex-col justify-between bg-white-50">
        <div className="mx-24 my-16 flex items-start gap-8">
          {card.icon?.url && <Image src={card.icon?.url} alt="" height={24} width={24} />}
          <div className="flex flex-col gap-4">
            <Subheading classes="text-black-100" bold>
              {getOr(card.title, '')}
            </Subheading>
            <div className="flex flex-col gap-8 whitespace-break-spaces">
              <RichTextContainer data={markdownDocument} />
              {card.linkButton && (
                <NextLink label={getOr(card.linkButton?.title, '')} href={getOr(card.linkButton?.uri, '')} />
              )}
            </div>
          </div>
        </div>
        {card.button && (
          <div className="flex items-center justify-between gap-8 rounded-b-28 px-16 py-16 sm:px-24">
            {card.price && (
              <div>
                <Subheading small classes="text-red-100">
                  {card.pricefrom ? 'Verð frá:' : 'Verð:'}
                </Subheading>
                <div className="flex items-end gap-4">
                  <ISKFormat
                    value={card.price}
                    renderText={(value) => (
                      <Subheading bold classes="text-red-100">
                        {value}
                      </Subheading>
                    )}
                    showSuffix={true}
                  />
                </div>
              </div>
            )}
            <CardButton
              id={card.id}
              buttonData={card.button}
              autoWidth={Boolean(card.price)}
              onClick={onClick}
              animateOnSuccess={animateOnSuccess}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Card;
